/**
 * 1.1, a third time — the ink of a signature, lifted off a picture of it.
 *
 * Nitzan, 17.09: the sender's signature can come from a picture they already
 * have. A photographed signature is mostly paper, grey and grainy and darker at
 * one end than the other, and kept as it is that paper prints onto the document
 * as a grey patch and is far too big for the server's 32KB. So only the ink is
 * kept, in one colour, with only how much of it shows changing from pixel to
 * pixel: that compresses to a few kilobytes.
 *
 * Paper is told from ink by how much darker a pixel is than the brightest paper
 * near it, not by one cut-off for the whole picture. That is what keeps the
 * shaded end of a photographed page from reading as ink.
 */

/** Where the ink is, in pixels of the picture. */
export interface InkBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Darker than the paper around it by less than this, a pixel is grain or shade. */
const PAPER = 0.15;
/** Darker by this much more, it is ink all the way through. */
const INK_RAMP = 0.25;
/** Paper is never this dark: around a pixel with nothing brighter than it, there is no paper to lift ink off. */
const DARKEST_PAPER = 60;
/** The luminance ink is kept at or under, the darkness a pen line reads at on paper. */
const INK_LUMINANCE = 60;
/** How many pixels a row or column needs before it counts as signature, not dust. */
const DUST = 3;

const luminance = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b;

/**
 * The brightest of `from` within `radius` of each of `count` values read `step`
 * apart from `start`, written to the same places in `into`: a sliding maximum,
 * one pass along a row or a column.
 */
function brightestNear(
  from: Float32Array,
  into: Float32Array,
  start: number,
  step: number,
  count: number,
  radius: number,
  queue: Int32Array,
) {
  let head = 0;
  let tail = 0;
  for (let i = 0; i < count + radius; i += 1) {
    if (i < count) {
      const value = from[start + i * step]!;
      while (tail > head && from[start + queue[tail - 1]! * step]! <= value) tail -= 1;
      queue[tail] = i;
      tail += 1;
    }
    if (queue[head]! < i - 2 * radius) head += 1;
    const centre = i - radius;
    if (centre >= 0) into[start + centre * step] = from[start + queue[head]! * step]!;
  }
}

/** Keep coherent strokes and their soft edges, rather than isolated camera grain. */
function removeGrain(ink: Uint8ClampedArray, width: number, height: number) {
  const count = width * height;
  const seen = new Uint8Array(count);
  const keep = new Uint8Array(count);
  const pending = new Int32Array(count);
  const minimumStroke = Math.max(12, Math.round(Math.max(width, height) / 60));
  for (let start = 0; start < count; start += 1) {
    if (seen[start] || ink[start * 4 + 3]! < 128) continue;
    let head = 0;
    let tail = 1;
    let strong = false;
    seen[start] = 1;
    pending[0] = start;
    while (head < tail) {
      const at = pending[head++]!;
      if (ink[at * 4 + 3]! >= 224) strong = true;
      const x = at % width;
      const y = Math.floor(at / width);
      for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
        for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
          const next = ny * width + nx;
          if (seen[next] || ink[next * 4 + 3]! < 128) continue;
          seen[next] = 1;
          pending[tail++] = next;
        }
      }
    }
    // Small dark dots belong to handwriting too. Low contrast paper specks do not.
    if (tail < minimumStroke && !(strong && tail >= 3)) continue;
    for (let index = 0; index < tail; index += 1) {
      const at = pending[index]!;
      const x = at % width;
      const y = Math.floor(at / width);
      for (let ny = Math.max(0, y - 2); ny <= Math.min(height - 1, y + 2); ny += 1) {
        for (let nx = Math.max(0, x - 2); nx <= Math.min(width - 1, x + 2); nx += 1) keep[ny * width + nx] = 1;
      }
    }
  }
  for (let i = 0; i < count; i += 1) if (!keep[i]) ink[i * 4 + 3] = 0;
}

/**
 * The picture's ink on a clear background, and where in the picture the ink
 * is; null bounds when there is none. `pixels` is RGBA, row by row, as a canvas
 * hands it out, and is not changed. See-through parts are read as lying on
 * white paper.
 */
export function inkOnly(pixels: Uint8ClampedArray, width: number, height: number) {
  const count = width * height;
  const light = new Float32Array(count);
  const red = new Float32Array(count);
  const green = new Float32Array(count);
  const blue = new Float32Array(count);
  for (let i = 0; i < count; i += 1) {
    const cover = pixels[i * 4 + 3]! / 255;
    const paper = 255 * (1 - cover);
    red[i] = pixels[i * 4]! * cover + paper;
    green[i] = pixels[i * 4 + 1]! * cover + paper;
    blue[i] = pixels[i * 4 + 2]! * cover + paper;
    light[i] = luminance(red[i]!, green[i]!, blue[i]!);
  }

  // The paper under each pixel: the brightest pixel in the square around it.
  // The square is wider than any pen line, so it always reaches paper.
  const radius = Math.max(8, Math.round(Math.max(width, height) / 25));
  const queue = new Int32Array(Math.max(width, height));
  const across = new Float32Array(count);
  const paper = new Float32Array(count);
  for (let y = 0; y < height; y += 1) brightestNear(light, across, y * width, 1, width, radius, queue);
  for (let x = 0; x < width; x += 1) brightestNear(across, paper, x, width, height, radius, queue);

  const cover = new Float32Array(count);
  let inkRed = 0;
  let inkGreen = 0;
  let inkBlue = 0;
  let inkWeight = 0;
  for (let i = 0; i < count; i += 1) {
    const under = paper[i]!;
    const darker = under < DARKEST_PAPER ? 0 : (under - light[i]!) / under;
    const shows = Math.min(1, Math.max(0, (darker - PAPER) / INK_RAMP));
    cover[i] = shows;
    // The colour is read off the ink that is ink all the way through; the
    // edges of a line are part paper.
    const weight = shows === 1 ? 1 : 0;
    inkRed += red[i]! * weight;
    inkGreen += green[i]! * weight;
    inkBlue += blue[i]! * weight;
    inkWeight += weight;
  }

  let [r, g, b] = inkWeight > 0 ? [inkRed / inkWeight, inkGreen / inkWeight, inkBlue / inkWeight] : [0, 0, 0];
  const lightness = luminance(r, g, b);
  if (lightness > INK_LUMINANCE) {
    // A pen photographed washed out is kept its own colour, at a pen's darkness.
    const darken = INK_LUMINANCE / lightness;
    [r, g, b] = [Math.floor(r * darken), Math.floor(g * darken), Math.floor(b * darken)];
  } else {
    [r, g, b] = [Math.round(r), Math.round(g), Math.round(b)];
  }

  const ink = new Uint8ClampedArray(count * 4);
  const perRow = new Int32Array(height);
  const perColumn = new Int32Array(width);
  for (let i = 0; i < count; i += 1) {
    const alpha = Math.round(cover[i]! * 255);
    ink[i * 4] = r;
    ink[i * 4 + 1] = g;
    ink[i * 4 + 2] = b;
    ink[i * 4 + 3] = alpha;
  }
  removeGrain(ink, width, height);
  for (let i = 0; i < count; i += 1) {
    if (ink[i * 4 + 3]! >= 128) {
      perRow[Math.floor(i / width)]! += 1;
      perColumn[i % width]! += 1;
    }
  }

  const signature = (inked: number) => inked >= DUST;
  let top = perRow.findIndex(signature);
  let left = perColumn.findIndex(signature);
  if (top < 0 || left < 0) return { pixels: ink, bounds: null };
  // Walked back by hand: `findLastIndex` is newer than some phones' web views.
  let bottom = height - 1;
  while (!signature(perRow[bottom]!)) bottom -= 1;
  let right = width - 1;
  while (!signature(perColumn[right]!)) right -= 1;

  // The rows and columns thick with ink miss where a line thins out as the pen
  // lifts. So the edges move out for as long as the next row or column over
  // carries ink on, and stop at the first one with none: a speck further out,
  // even in line with the signature, stays out.
  const inked = (x: number, y: number) => ink[(y * width + x) * 4 + 3]! >= 128;
  const inkAcross = (y: number) => {
    for (let x = left; x <= right; x += 1) if (inked(x, y)) return true;
    return false;
  };
  const inkDown = (x: number) => {
    for (let y = top; y <= bottom; y += 1) if (inked(x, y)) return true;
    return false;
  };
  for (let grew = true; grew; ) {
    grew = false;
    if (left > 0 && inkDown(left - 1)) [left, grew] = [left - 1, true];
    if (right < width - 1 && inkDown(right + 1)) [right, grew] = [right + 1, true];
    if (top > 0 && inkAcross(top - 1)) [top, grew] = [top - 1, true];
    if (bottom < height - 1 && inkAcross(bottom + 1)) [bottom, grew] = [bottom + 1, true];
  }

  return { pixels: ink, bounds: { x: left, y: top, width: right - left + 1, height: bottom - top + 1 } as InkBounds | null };
}
